<script setup lang="ts">
  import useModals from '@leaflink/stash/useModals.vue';
  import { storeToRefs } from 'pinia';
  import { computed, onBeforeMount, ref } from 'vue';

  import DirectPayTermsConfirmation from '@/components/TermsAndConditions/components/DirectPayTermsConfirmation.vue';
  import LinkABankCta from '@/components/TermsAndConditions/components/LinkABankCta.vue';
  import { useCompanyStore } from '@/piniaStores/company/company.store';
  import { useUserStore } from '@/piniaStores/user/user.store';

  import SignupDirectPayModal from './components/SignupDirectPayModal.vue';

  const userStore = useUserStore();
  const { user, userActiveCompanyPermissions } = storeToRefs(userStore);

  const companyStore = useCompanyStore();
  const { pendingPaymentProgramRequests } = storeToRefs(companyStore);

  const modal = useModals();

  const isLoading = ref(true);

  const newPendingRequests = computed(() => {
    return pendingPaymentProgramRequests.value.filter((request) => {
      if (!user.value.id) return false;

      return !request.seenBy.includes(user.value.id);
    });
  });

  const showSignupDirectPay = computed(() => {
    if (!companyStore.activeCompany || !userActiveCompanyPermissions.value?.staffIsAdmin) {
      return false;
    }

    const isOnlySeller = !companyStore.activeCompany.buyer && companyStore.activeCompany.seller;

    return isOnlySeller && newPendingRequests.value.length;
  });

  onBeforeMount(async () => {
    await companyStore.fetchCompanyPaymentProgramChangeRequests();
    isLoading.value = false;

    if (showSignupDirectPay.value) {
      modal.open({
        component: SignupDirectPayModal,
        attributes: {
          onDismiss: () => {
            markAsSeen();
            modal.close();
          },
          onSignUp: () => {
            modal.close();
            markAsSeen();
            openTermsAndConditions();
          },
        },
      });
    }
  });

  function openTermsAndConditions() {
    modal.open({
      component: DirectPayTermsConfirmation,
      attributes: {
        isOpen: true,
        onAccepted: () => {
          modal.close();
          openBankLinking();
        },
        onDismiss: () => {
          modal.close();
        },
      },
    });
  }

  function openBankLinking() {
    modal.open({
      component: LinkABankCta,
      attributes: {
        isOpen: true,
        onDismiss: () => modal.close(),
      },
    });
  }

  function markAsSeen() {
    const ids = newPendingRequests.value.map((request) => {
      return request.publicId;
    });

    companyStore.updateCompanyPaymentProgramChangeRequest(ids);
  }
</script>

<template>
  <!-- this is necessary to get tests to pass without false positives -->
  <div v-if="isLoading" data-test="direct-pay-signup-loading"></div>
</template>
